import { customElement } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { nextUntil } from '../../utils/ids-dom-utils/ids-dom-utils';
import { injectTemplate, stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import IdsElement from '../../core/ids-element';
import type IdsDataGrid from './ids-data-grid';
import type { IdsDataGridColumn } from './ids-data-grid-column';
import IdsDataGridCell from './ids-data-grid-cell';

@customElement('ids-data-grid-row')
export default class IdsDataGridRow extends IdsElement {
  rootNode?: any;

  constructor() {
    super({ noShadowRoot: true });
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.DRAGGABLE,
      attributes.DISABLED,
      attributes.ROW_INDEX,
      attributes.DATA_INDEX
    ];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;

    if (name === attributes.ROW_INDEX) {
      this.renderRow(Number(newValue));
    }
  }

  connectedCallback(): void {
    // NOTE: bypassing super.connectedCallback() for performance reasons
  }

  /**
   * Reference to the data grid parent
   * @returns {IdsDataGrid} the data grid parent
   */
  get dataGrid() {
    if (!this.rootNode) this.rootNode = (this.getRootNode() as any).host;
    return (this.rootNode) as IdsDataGrid;
  }

  get data(): Record<string, any>[] {
    return this.dataGrid?.data || [];
  }

  get rowData(): Record<string, any> {
    return this.data[this.rowIndex] ?? {};
  }

  get columns(): IdsDataGridColumn[] {
    return this.dataGrid?.columns || [];
  }

  get visibleColumns(): IdsDataGridColumn[] {
    return this.dataGrid?.visibleColumns || [];
  }

  /* Returns all the editable cell elements in an array */
  get cellsEditable(): IdsDataGridCell[] {
    // NOTE: Array.from() seems slower than dotdotdot array-destructuring.
    return [
      ...this.querySelectorAll<IdsDataGridCell>('ids-data-grid-cell.is-editable:not(.is-readonly,.is-disabled)')
    ];
  }

  get isRowDisabled(): ((rowIndex: number, rowData: Record<string, any>) => boolean) | undefined {
    return this.dataGrid?.isRowDisabled;
  }

  get dimensions() {
    return this.getBoundingClientRect();
  }

  get expandIcon() {
    return this.querySelectorAll('.ids-data-grid-tree-container ids-button ids-icon');
  }

  /**
   * Set the row disabled state.
   * @param {boolean | string} value the value
   */
  set disabled(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.DISABLED, '');
      this.setAttribute('aria-disabled', 'true');
      this.rowData.disabled = true;
    } else {
      this.removeAttribute(attributes.DISABLED);
      this.removeAttribute('aria-disabled');
      this.rowData.disabled = false;
    }
  }

  get disabled(): boolean | string { return this.hasAttribute(attributes.DISABLED); }

  /**
   * Set the row index. This index will be used to populate data from ids-data-grid.
   * @param {number} value the index
   */
  set rowIndex(value: number) {
    if (value !== null && value >= 0) {
      this.setAttribute(attributes.ROW_INDEX, String(value));
    } else {
      this.removeAttribute(attributes.ROW_INDEX);
    }
  }

  /**
   * Gets the row index # of this row.
   * @returns {number} the row-index
   */
  get rowIndex(): number { return Number(this.getAttribute(attributes.ROW_INDEX) ?? -1); }

  /** Implements row cache */
  static rowCache = new Map<string, string>();

  get cache() {
    return IdsDataGridRow.rowCache;
  }

  /**
   * Return the row from the cache or template.
   * @param {number} row the row index
   * @returns {string} the row's html
   */
  cacheRow(row: number) {
    const dataGrid = this.dataGrid;
    const cacheHash = dataGrid.cacheHash;
    const rowIndex = Number(row);
    const selectState = dataGrid.data[row].rowSelected ? dataGrid.cacheSelected : dataGrid.cacheDeselected;
    const cacheKey = `${cacheHash}:${rowIndex}:${selectState}`;

    // This is current cache strategy via memoization.
    if (!IdsDataGridRow.rowCache.has(cacheKey)) {
      IdsDataGridRow.rowCache.set(cacheKey, this.cellsHTML());
    }
    return IdsDataGridRow.rowCache.get(cacheKey)!;
  }

  /**
   * Render the row again from the cache or template.
   * @param {number} row the row index
   */
  renderRow(row: number) {
    const cells = [...this.children] as IdsDataGridCell[];

    if (this.isRowDisabled && (this.rowData.disabled === undefined || this.rowData.disabled === null)) {
      this.disabled = this.isRowDisabled(row, this.rowData);
    }

    // Re-render datagrid cells instead datagrid rows for performance
    if (cells.length === this.visibleColumns.length) {
      [...cells].forEach((cell) => cell?.renderCell?.());
    } else {
      this.innerHTML = this.cacheRow(row);
    }
  }

  /**
   * Refreshes the row's cells
   */
  refreshRow(): void {
    this.querySelectorAll<IdsDataGridCell>(':scope > ids-data-grid-cell').forEach((cell) => cell.refreshCell());
    this.#setAttributes();
  }

  /**
   * Validate all cells in this row
   */
  validateCells(): void {
    this.querySelectorAll<IdsDataGridCell>(':scope > ids-data-grid-cell').forEach((cell) => cell.validateCell());
  }

  /** Set row attributes and classes */
  #setAttributes() {
    // Find max column height and apply to all cells in the row
    const maxHeight = Math.max(...this.visibleColumns.map((col) => col.height || 0));
    const cells = this.querySelectorAll<IdsDataGridCell>('ids-data-grid-cell');
    if (maxHeight > 0) {
      cells.forEach((cell) => {
        // Skip textarea formatter cells as they have auto height
        if (!cell.classList.contains('formatter-textarea')) {
          cell.style.setProperty('height', `${maxHeight}px`, 'important');
          cell.style.setProperty('align-items', 'center', 'important');
        }
      });
    } else {
      // Cleanup: remove custom heights if no column height is set
      cells.forEach((cell) => {
        if (cell.style.getPropertyPriority('height') === 'important') {
          cell.style.removeProperty('height');
          cell.style.removeProperty('align-items');
        }
      });
    }
    const rowIndex = this.rowIndex;
    const rowData = this.rowData;
    const rowExpanded = this.isExpanded();
    let iconType = rowExpanded ? `plusminus-folder-open` : `plusminus-folder-closed`;
    if (this.dataGrid?.expandableRow) {
      iconType = rowExpanded ? `caret-up` : `caret-down`;
    }
    this.setAttribute('data-index', String(rowIndex));
    this.setAttribute('aria-rowindex', String(rowIndex + 1));

    if (rowData?.rowHidden) {
      this.hidden = true;
      this.setAttribute('hidden', '');
    } else {
      this.hidden = false;
      this.removeAttribute('hidden');
    }

    if (rowData?.rowSelected) {
      this.selected = rowData.rowSelected;
    }
    if (!rowData?.rowSelected && this.classList.contains('selected')) {
      this.selected = rowData.rowSelected;
    }

    if (this.dataGrid?.expandableRow) {
      this.setAttribute('aria-expanded', String(!!rowExpanded));
      this.expandIcon?.forEach((icon) => icon.setAttribute('icon', iconType));

      const childRow = this.querySelector<HTMLElement>('.ids-data-grid-expandable-row');
      if (rowExpanded) {
        childRow?.removeAttribute('hidden');
      } else {
        childRow?.setAttribute('hidden', '');
      }
    }

    if (this.dataGrid?.groupable && rowData?.isGroupRow) {
      this.classList.add('is-group-row');
      this.setAttribute('aria-expanded', String(!!rowExpanded));
      if (rowData?.groupCollapsed) {
        this.querySelectorAll('.expand-button ids-icon').forEach((icon) => {
          icon?.setAttribute('icon', 'plusminus-folder-closed');
        });
      }
    }

    if (this.dataGrid?.treeGrid) {
      this.setAttribute('aria-setsize', rowData?.ariaSetSize);
      this.setAttribute('aria-level', rowData?.ariaLevel);
      this.setAttribute('aria-posinset', rowData?.ariaPosinset);

      if (rowData?.children?.length) {
        this.setAttribute('aria-expanded', String(!!rowExpanded));
      } else {
        // End nodes, nodes with no descendant nodes, should not have the aria-expanded attribute because,
        // if they were to have it, they would be incorrectly described to assistive technologies as parent nodes.
        // @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-expanded#treeitems
        this.removeAttribute('aria-expanded');
      }

      if ((rowData?.children as any)?.length) {
        this.expandIcon?.forEach((icon) => icon.setAttribute('icon', iconType));
      }
    }
  }

  /**
   * Toggle Selection on the row element (via click/keyboard in the main dataGrid)
   */
  toggleSelection() {
    this.dataGrid?.resetCache();
    const isSelected = this.classList.contains('selected');
    const index = Number(this.getAttribute('data-index'));

    if (isSelected && !this.dataGrid?.suppressRowDeselection) this.dataGrid?.deSelectRow(index);
    else this.dataGrid?.selectRow(index);

    this.dataGrid?.triggerEvent('selectionchanged', this.dataGrid, {
      detail: {
        elem: this,
        selectedRows: this.dataGrid?.selectedRows
      }
    });
  }

  /**
   * Toggle Expand/Collpase on the row element
   * @param {boolean} triggerEvent If true, will trigger event
   */
  toggleExpandCollapse(triggerEvent = true) {
    const isExpanded = this.isExpanded();
    const shouldCollapse = isExpanded === true;
    const shouldExpand = !shouldCollapse;

    if (shouldExpand) {
      this.doExpand();
    }
    if (shouldCollapse) {
      this.doCollapse();
    }

    if (triggerEvent) {
      const eventName = shouldExpand ? 'rowexpanded' : 'rowcollapsed';
      this.dataGrid?.triggerEvent(eventName, this.dataGrid, {
        bubbles: true,
        detail: {
          elem: this,
          row: this.rowIndex,
          data: this.rowData,
        }
      });
    }
  }

  /**
   * Toggle activation on the row element
   */
  toggleRowActivation() {
    const isActivated = this.classList.contains('activated');
    const currentRow = Number(this.getAttribute('aria-rowindex')) - 1;

    if (isActivated && !this.dataGrid?.suppressRowDeactivation) {
      this.dataGrid?.deactivateRow(currentRow);
    } else {
      if (this.dataGrid?.activatedRow) this.dataGrid?.deactivateRow(this.dataGrid?.activatedRow.index);
      this.dataGrid?.activateRow(currentRow);
    }

    this.dataGrid?.triggerEvent('activationchanged', this.dataGrid, {
      detail: {
        elem: this,
        data: this.dataGrid?.activatedRow,
        row: Number(this.getAttribute('data-index'))
      }
    });
  }

  /**
   * Select/Deselect all child rows
   * @param {boolean} isSelect true or false to select or deselect
   * @private
   */
  toggleChildRowSelection(isSelect: boolean) {
    const level = this?.getAttribute('aria-level');

    nextUntil(this, `[aria-level="${level}"]`).forEach((childRow) => {
      const nodeLevel = Number(childRow.getAttribute('aria-level'));
      if (nodeLevel > Number(level) && this.dataGrid) {
        this.dataGrid.groupSelectsChildren = false;
        if (isSelect) this.dataGrid?.selectRow(Number(childRow.getAttribute('data-index')));
        else this.dataGrid?.deSelectRow(Number(childRow.getAttribute('data-index')));
        this.dataGrid.groupSelectsChildren = true;
      }
    });
  }

  /**
   * Hide/Show all child rows for groupable rows and fire events
   */
  toggleGroupChildRows() {
    const rowData = this.data[Number(this.dataset.index)];
    rowData.groupCollapsed = rowData.groupCollapsed || false;

    const noChildren = rowData.groupChildCount;
    let childRow = this as IdsDataGridRow;
    for (let i = 0; i < noChildren; i++) {
      childRow = childRow.nextElementSibling as any;
      if (!childRow) break;

      childRow.hidden = !rowData.groupCollapsed;
      this.data[Number(childRow.dataset.index)].rowHidden = !rowData.groupCollapsed;
    }

    this.querySelector('.expand-button')?.classList.toggle('expanded');
    rowData.groupCollapsed = !rowData.groupCollapsed;

    this.querySelectorAll('.expand-button ids-icon')?.forEach((icon) => {
      icon?.setAttribute('icon', `plusminus-folder-${rowData.groupCollapsed ? 'closed' : 'open'}`);
    });
  }

  /**
   * Select this row node
   */
  set selected(select: boolean) {
    if (select) {
      this.classList.add('selected');
      this.setAttribute('aria-selected', 'true');

      if (this.dataGrid?.rowSelection === 'mixed') {
        this?.classList.add('mixed');
      }
    } else {
      this.classList.remove('selected');
      this.removeAttribute('aria-selected');
    }
  }

  get selected(): boolean {
    return this.classList.contains('selected');
  }

  get recordIndex(): number | null {
    const rowData = this.rowData;

    let recordIndex = null;
    if (rowData !== undefined) {
      const keyValue = rowData[this.dataGrid.datasource.primaryKey];
      if (keyValue !== undefined) {
        const keyPair = this.dataGrid.datasource.primaryKey;
        recordIndex = this.dataGrid.datasource.getRecordIndex(keyPair, keyValue);
      }
    }
    return recordIndex;
  }

  /**
   * Updates some attributes/classes on a single row's cells
   * @private
   * @param {number} index the row index
   */
  updateCells(index: number) {
    const row = this;
    const cells = row.children;
    const rowData = this.rowData;

    if (cells?.length) {
      [...cells].forEach((cell: Element, columnIndex: number) => {
        const columnData = this.dataGrid?.columns[columnIndex];
        let cssPart = columnData?.cssPart || 'cell';

        // Updates selected rows to display the correct CSS part (also activated rows in mixed-selection mode)
        if (
          (this.dataGrid?.rowSelection === 'mixed' && row.classList.contains('activated'))
          || ((this.dataGrid?.rowSelection === 'single' || this.dataGrid?.rowSelection === 'multiple') && row.classList.contains('selected'))
        ) {
          if (columnData?.cellSelectedCssPart) cssPart = columnData?.cellSelectedCssPart;
          else cssPart = 'cell-selected';
        }

        if (typeof cssPart === 'function') {
          cssPart = cssPart({
            index,
            columnIndex,
            recordIndex: this.recordIndex,
            rowData
          });
        }
        cell.setAttribute('part', cssPart);
      });
    }
  }

  /**
   * Return the row's markup
   * @param {Record<string, unknown>} row The row data object
   * @param {number} index The data row index
   * @param {number} ariaRowIndex The indexes for aria-rowindex
   * @param {IdsDataGrid} dataGrid The dataGrid instance
   * @returns {string} The html string for the row
   */
  static template(row: Record<string, unknown>, index: number, ariaRowIndex: number, dataGrid: IdsDataGrid): string {
    let rowClasses = `${row?.rowSelected ? ' selected' : ''}`;
    rowClasses += `${row?.rowSelected && dataGrid?.rowSelection === 'mixed' ? ' mixed' : ''}`;
    rowClasses += `${row?.rowActivated ? ' activated' : ''}`;

    let treeAttrs = '';
    if (dataGrid?.treeGrid) {
      treeAttrs += ` aria-setsize="${row.ariaSetSize}" aria-level="${row.ariaLevel}" aria-posinset="${row.ariaPosinset}"`;
      if ((row.children as any)?.length) {
        treeAttrs += (row.rowExpanded === false) ? ` aria-expanded="false"` : ` aria-expanded="true"`;
      }
    }

    if (dataGrid?.pagination === 'client-side' && dataGrid?.pageNumber > 1) {
      ariaRowIndex += (Number(dataGrid?.pageNumber) - 1) * Number(dataGrid?.pageSize);
    }

    const isHidden = row?.rowHidden ? ' hidden' : '';

    // Set disabled state thru key found in the dataset
    const isRowDisabled = (): boolean => {
      const isTrue = (v: any) => (typeof v !== 'undefined' && v !== null && ((typeof v === 'boolean' && v === true) || (typeof v === 'string' && v.toLowerCase() === 'true')));
      const disabled = row.disabled;
      return isTrue(typeof disabled === 'function' ? disabled(index, row) : disabled);
    };
    const canRowDisabled = isRowDisabled();
    const disabled = canRowDisabled ? ' disabled aria-disabled="true"' : '';

    // Add and remove after to cache a temp disabled key,
    // so no need to run multiple times when rendering columns to check row disabled state
    if (row && canRowDisabled) row.idstempcanrowdisabled = canRowDisabled;

    const rowState = row?.rowState !== undefined ? row.rowState : 'row';

    return `
      <ids-data-grid-row
        row-index="${index}"
        role="row"
        part="${rowState}"
        aria-rowindex="${ariaRowIndex}"
        data-index="${index}"
        ${isHidden}
        class="ids-data-grid-row${rowClasses}"
        ${treeAttrs}
        ${disabled}
      >
      </ids-data-grid-row>
    `;
  }

  /**
   * Check if cell should be skipped due to spanning
   * @param {number} rowIndex the row index
   * @param {string} columnId the column id
   * @returns {boolean} true if cell should be skipped
   */
  private shouldSkipCell(rowIndex: number, columnId: string): boolean {
    return this.dataGrid?.shouldSkipCell?.(rowIndex, columnId) || false;
  }

  /**
   * Get rowspan count for a cell
   * @param {string} columnId the column id
   * @returns {number} rowspan count
   */
  private getRowspan(columnId: string): number {
    return this.rowData.rowspan?.[columnId] || 1;
  }

  /**
   * Return the cells' markup
   * @returns {string} The html string for the row
   */
  cellsHTML(): string {
    return this.#cellsHTMLImpl();
  }

  #cellsHTMLImpl(): string {
    const rowIndex = this.rowIndex;
    const ariaRowIndex = rowIndex;
    const rowData = this.data[rowIndex];
    const dataGrid = this.dataGrid;

    if (rowData.isGroupRow) {
      const expandButton = `
        <ids-button tabindex="-1" class="expand-button">
          <ids-icon icon="plusminus-folder-open"></ids-icon>
        </ids-button>
      `;

      return `
        <ids-data-grid-cell
          role="gridcell"
          part="cell"
          class="ids-data-grid-cell is-group-cell"
          aria-colindex="1"
        >
          ${expandButton} ${rowData?.groupLabel}
        </ids-data-grid-cell>
      `;
    }

    let expandableRowHtml = '';
    if (dataGrid?.expandableRow) {
      const template = injectTemplate(dataGrid?.querySelector(`#${dataGrid?.expandableRowTemplate}`)?.innerHTML || '', rowData);
      expandableRowHtml = `<div class="ids-data-grid-expandable-row"${this.isExpanded() ? '' : ` hidden`}>${template}</div>`;
    }

    let skippedCells = 0;
    const cellsHtml = dataGrid?.visibleColumns.map((column: IdsDataGridColumn, columnIndex: number) => {
      // Skip cells that are covered by rowspan from previous rows
      if (this.shouldSkipCell(rowIndex, column.id)) {
        skippedCells++;
        return null;
      }

      const spanCount = this.getRowspan(column.id);
      const spanAttr = spanCount > 1 ? ` rowspan="${spanCount}"` : '';
      const hasSkippedCells = skippedCells > 0 ? ' skipped-cell' : '';
      const ariaRowspanAttr = spanCount > 1 ? ` aria-rowspan="${spanCount}"` : '';

      // Build custom attributes string from column.attributes
      let customAttrs = '';
      if (column.attributes && Array.isArray(column.attributes)) {
        column.attributes.forEach((attr) => {
          const attrValue = typeof attr.value === 'function'
            ? attr.value({ rowIndex, rowData, columnId: column.id })
            : attr.value;
          const parts = [attr.prefix, attrValue, attr.suffix].filter(Boolean);
          const finalValue = parts.join('-').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          customAttrs += ` ${attr.name}="${finalValue}"`;
        });
      }

      const content = IdsDataGridCell.template(rowData, column, ariaRowIndex, dataGrid);

      return `
        <ids-data-grid-cell
          role="gridcell"
          aria-colindex="${columnIndex + 1}"
          column-id="${column.id}"${spanAttr}${ariaRowspanAttr}${hasSkippedCells}${customAttrs}
        >
          ${content}
        </ids-data-grid-cell>
      `;
    }).join('');

    // Process rowspan data for tracking
    this.dataGrid?.processRowspan?.(this.rowData, rowIndex);

    // Remove temp disabled key
    if (rowData?.idstempcanrowdisabled) delete rowData.idstempcanrowdisabled;

    return `${cellsHtml}${expandableRowHtml}`;
  }

  /**
   * Get the cell HTMLElement
   * @param {number} columnIndex the zero-based column index
   * @returns {IdsDataGridCell} html element for cell
   */
  cellByIndex(columnIndex: number): IdsDataGridCell | null {
    columnIndex = Math.max(columnIndex, 0);
    if (columnIndex === 0) {
      return this.querySelector<IdsDataGridCell>('ids-data-grid-cell') ?? null;
    }

    const cells = this.querySelectorAll<IdsDataGridCell>('ids-data-grid-cell');
    const maxColumnIndex = cells.length - 1;
    columnIndex = Math.min(columnIndex, maxColumnIndex);

    return cells[columnIndex] ?? null;
  }

  /**
   * Get the cell HTMLElement by logical column index (accounting for rowspan)
   * @param {number} logicalColumnIndex the zero-based logical column index
   * @returns {IdsDataGridCell} html element for cell
   */
  cellByLogicalIndex(logicalColumnIndex: number): IdsDataGridCell | null {
    const columnId = this.dataGrid.visibleColumns[logicalColumnIndex]?.id;
    if (!columnId) return null;

    // If this cell is skipped due to rowspan, return null
    if (this.shouldSkipCell(this.rowIndex, columnId)) {
      return null;
    }

    // Find the actual cell by aria-colindex
    return this.querySelector<IdsDataGridCell>(`ids-data-grid-cell[aria-colindex="${logicalColumnIndex + 1}"]`) ?? null;
  }

  /**
   * Is this row currently expanded
   * @returns {boolean} true if expanded
   */
  isExpanded(): boolean {
    const rowData = this.dataGrid?.data[this.rowIndex];

    if (this.dataGrid?.expandableRow) {
      // expandableRows are collapsed by default, so only expand if rowExpanded is explicity "true"
      return rowData?.rowExpanded === true;
    }

    // all rows are expanded by default (i.e. tree-grid), unless rowExpanded is explicitly "false"
    return rowData?.rowExpanded !== false;
  }

  /**
   * Expand the row element
   */
  doExpand() {
    this.dataGrid?.updateDataset(this.rowIndex, { rowExpanded: true, rowHidden: false });

    // this.#setAttributes() will do this.setAttribute('aria-expanded', 'true');
    this.#setAttributes();

    if (this.dataGrid?.treeGrid) {
      const level = Number(this.getAttribute('aria-level')) || 1;
      const nextLevel = level + 1;
      let parentExpanded = this.isExpanded();

      nextUntil(this, `[aria-level="${level}"]`).forEach((childRow) => {
        const childAriaLevel = Number(childRow.getAttribute('aria-level')) || 1;
        const shouldExpand = (childAriaLevel === nextLevel) || parentExpanded;
        if (shouldExpand) {
          const childRowIndex = Number(childRow.getAttribute('row-index'));
          this.dataGrid?.updateDataset(childRowIndex, { rowHidden: false });
          childRow.removeAttribute('hidden');
          parentExpanded = (childRow as IdsDataGridRow).isExpanded?.();
        }
      });
    }
  }

  /**
   * Collapse the row element
   */
  doCollapse() {
    this.dataGrid?.updateDataset(this.rowIndex, { rowExpanded: false });

    // this.#setAttributes() will do this.setAttribute('aria-expanded', 'false');
    this.#setAttributes();

    if (this.dataGrid?.treeGrid) {
      const level = Number(this.getAttribute('aria-level')) || 1;

      nextUntil(this, `[aria-level="${level}"]`).forEach((childRow) => {
        const childAriaLevel = Number(childRow.getAttribute('aria-level')) || 1;
        if (childAriaLevel > level) {
          const childRowIndex = Number(childRow.getAttribute('row-index'));
          this.dataGrid?.updateDataset(childRowIndex, { rowHidden: true });
          childRow?.setAttribute('hidden', '');
        }
      });
    }
  }
}
